import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '@/redux/common/baseQuery';

interface ApiResponse<T> {
  success: boolean;
  message_code: number;
  message: string;
  data: T;
  total?: number;
}

export interface AgentTool {
  tool_id?: string;
  agent_tool_id?: string;
  name?: string;
  tool_name?: string;
  description?: string;
  argument_schema?: Record<string, unknown>;
  toolkit_name?: string;
  toolkit_type?: string;
  requires_confirmation?: boolean;
  action_card_template?: unknown;
  enabled_preview?: boolean;
  preview_tool?: unknown;
  /** NOT per-agent — lives on the tools catalogue entry itself (edited from the Tool catalogue
   * section, not per-agent attachment), shared by every agent that attaches this tool.
   * {status, status_message, data} is ALWAYS folded into the tool's description on the backend
   * (documentation) AND doubles as the live template source below. status_message templating has
   * no flag — it's always applied on a SUCCESS result whenever status_message is set (rendering a
   * template is safe unconditionally: no {{ }} placeholders means an untouched no-op). Only the
   * `data`-reshaping flags below stay opt-in, since replacing `data` wholesale is materially
   * riskier than a status-message no-op. */
  output_schema?: { status?: string; status_message?: string; data?: unknown };
  /** Renders output_schema.data as a Jinja2 template whose output must be valid JSON, REPLACING
   * `data` wholesale — can rename keys, compute/format values, reshape lists. DANGEROUS to enable
   * while output_schema.data is still a plain documentation sample with no {{ }} placeholders:
   * every call would then silently return that same hardcoded example instead of real data. Only
   * turn on after rewriting output_schema.data into an actual template. */
  apply_data_template?: boolean;
  /** Simpler/safer alternative to apply_data_template: keeps only the keys output_schema.data
   * declares (missing → null, extras dropped) — no template authoring needed. Enable at most one
   * of apply_data_template / apply_data_projection per tool; the template already controls shape. */
  apply_data_projection?: boolean;
  /** Per-tool documentation-only allowlist of merge variables usable in this tool's templates
   * above — name -> plain-English description. Computes nothing itself; folded into the
   * description alongside output_schema for both the admin authoring a template and the LLM. */
  output_variables?: Record<string, string>;
  /** Free-form admin-added {name: Jinja2 template} pairs — NOT a single fixed field, any number of
   * arbitrarily-named ones (e.g. "llm_instruction" for the model, or anything a frontend card
   * wants to read off the result). Each renders fresh against the call's real `data` on every
   * result (success, failure, cancelled) and merges into the result as result[name]. Blank render
   * for a key just omits it; a render failure is silently skipped, never blocks the call. */
  custom_fields?: Record<string, string>;

  // ── Per-agent overrides (attached-tool level, NOT catalogue) ───────────────────────────────
  // Every field above defaults from the shared tools catalogue entry — edited from the Tool
  // catalogue section, one definition for every agent that attaches this tool. The 4 fields below
  // are the opt-in per-agent override for when the SAME tool call genuinely needs a different
  // summary/instruction for THIS agent specifically (different persona, different phrasing).
  // `undefined`/`null` (the default) means "inherit the catalogue value" — leave it unset unless
  // this agent actually needs to diverge. Resolved server-side in
  // app.services.llm.functions.parse_tools._merge_catalogue_overrides. No
  // apply_status_message_template_override — status_message templating has no flag to override.
  output_schema_override?: { status?: string; status_message?: string; data?: unknown } | null;
  apply_data_template_override?: boolean | null;
  apply_data_projection_override?: boolean | null;
  custom_fields_override?: Record<string, string> | null;
}

/** A point-in-time snapshot of an agent's prompt/config, persisted in the agent record. */
export interface AgentVersion {
  id: string;
  n: number;
  message: string;
  created_at: string;
  created_by?: string;
  snapshot: Record<string, unknown>;
}

export interface ModelsConfig {
  text: string;
  realtime?: string | null;
}

export interface Agent {
  agent_id: string;
  agent_name: string;
  description?: string;
  background?: string;
  role_of_the_agent?: string;
  company_information?: string;
  agent_response_structure?: string;
  models: ModelsConfig;
  status: string;
  /** "conversational" (chat-style) or "workflow" (trigger-driven automation) */
  agent_type?: 'conversational' | 'workflow';
  /** How a workflow agent is triggered */
  trigger_type?: 'manual' | 'schedule' | 'webhook' | 'event';
  /** Prompt/config version history (round-trips via the loose backend schema). */
  versions?: AgentVersion[];
  published_version_id?: string;
  /** Dotted context tokens auto-injected into the system prompt (e.g. "user.timezone"). */
  prompt_variables?: string[];
  tenant_id?: string | null;
  icon_url?: string;
  category?: string;
  tags?: string[];
  tools: AgentTool[];
  tool_scope_overrides?: ToolScopeOverrides;
  audio_prompt?: string;
  system_prompts: Array<{
    id?: string;
    title?: string;
    prompt: string;
    order?: number;
    category?: string;
    condition_roles?: string[];
    /** SamModule slugs this block is scoped to (see ScreenInfo['module']) — empty/absent = always injected. */
    condition_modules?: string[];
  }>;
  persistent_views: Record<string, unknown>[];
  data_store_config: Record<string, unknown>[];
  email_listeners: Record<string, unknown>[];
  model_parameters: Record<string, unknown>;
  voice: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}

export type AgentUpsert = Partial<Agent> & { agent_name: string; models: ModelsConfig };

export interface SupportedModel { model_id: string; name: string; description?: string }
export interface Toolkit { toolkit_id: string; toolkit_name: string; toolkit_type: string; description?: string }
export interface ParameterDescriptor {
  key: string; label: string; type: string; default?: unknown;
  min?: number; max?: number; options?: string[]; applies_to?: string;
}
export interface ParameterCatalogue { groups: Record<string, ParameterDescriptor[]> }

export interface AgentFile {
  file_id: string;
  name: string;
  url: string;
  type?: string;
  size?: number;
  file_type?: string;
  /** ingestion pipeline status — pending | processing | completed | failed */
  ingestion_status?: 'pending' | 'processing' | 'completed' | 'failed';
}

export interface AgentRun {
  run_id: string;
  status: 'pending' | 'running' | 'completed' | 'error' | 'timeout';
  started_at?: string;
  ended_at?: string;
  /** Duration in milliseconds, if available from the API */
  latency_ms?: number;
  trigger_type?: string;
}

/** One SAM workspace screen (module): `tools` is code-derived (page_tools.py TOOL_GROUPS) and
 * read-only. The additive admin layer is per-agent — `Agent.tool_scope_overrides`. */
export interface ScreensInfo { core_tools: string[]; available_tools: string[] }
export interface ToolScopeOverrides { global_tools: string[]; screens: Record<string, string[]> }

interface ListAgentsArgs { search?: string; status?: string; model?: string; skip?: number; limit?: number }

/** What the model is actually handed for one screen: the compiled prompt AND the scoped tool
 *  set. Tool descriptions + argument schemas are the larger half of a turn, so a prompt-only
 *  preview hides most of the cost. */
export interface PromptPreview {
  system_prompt: string;
  tools: Array<{ name: string; description: string; argument_schema: Record<string, unknown> }>;
  usage: { prompt_chars: number; tool_chars: number; total_chars: number; tool_count: number };
}

export const agentsApi = createApi({
  reducerPath: 'agentsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Agent', 'AgentList', 'ToolCatalogue'],
  endpoints: (builder) => ({
    listAgents: builder.query<{ data: Agent[]; total: number }, ListAgentsArgs | void>({
      query: (args) => {
        const p = new URLSearchParams();
        const a = args || {};
        if (a.search) p.set('search', a.search);
        if (a.status) p.set('status', a.status);
        if (a.model) p.set('model', a.model);
        if (a.skip != null) p.set('skip', String(a.skip));
        if (a.limit != null) p.set('limit', String(a.limit));
        const qs = p.toString();
        return `/api/v1/agents${qs ? `?${qs}` : ''}`;
      },
      transformResponse: (res: ApiResponse<Agent[]>) => ({ data: res.data, total: res.total ?? res.data.length }),
      providesTags: ['AgentList'],
    }),
    getAgent: builder.query<Agent, string>({
      query: (id) => `/api/v1/agents/${id}`,
      transformResponse: (res: ApiResponse<Agent>) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'Agent', id }],
    }),
    createAgent: builder.mutation<Agent, AgentUpsert>({
      query: (body) => ({ url: '/api/v1/agents', method: 'POST', body }),
      transformResponse: (res: ApiResponse<Agent>) => res.data,
      invalidatesTags: ['AgentList'],
    }),
    updateAgent: builder.mutation<Agent, { id: string; body: AgentUpsert }>({
      query: ({ id, body }) => ({ url: `/api/v1/agents/${id}`, method: 'PUT', body }),
      transformResponse: (res: ApiResponse<Agent>) => res.data,
      invalidatesTags: (_r, _e, { id }) => ['AgentList', { type: 'Agent', id }],
    }),
    setAgentStatus: builder.mutation<Agent, { id: string; status: string }>({
      query: ({ id, status }) => ({ url: `/api/v1/agents/${id}/status`, method: 'PATCH', body: { status } }),
      transformResponse: (res: ApiResponse<Agent>) => res.data,
      invalidatesTags: ['AgentList'],
    }),
    deleteAgent: builder.mutation<void, string>({
      query: (id) => ({ url: `/api/v1/agents/${id}`, method: 'DELETE' }),
      invalidatesTags: ['AgentList'],
    }),
    cloneAgent: builder.mutation<Agent, { id: string; agent_name?: string }>({
      query: ({ id, agent_name }) => ({ url: `/api/v1/agents/${id}/copies`, method: 'POST', body: { agent_name } }),
      transformResponse: (res: ApiResponse<Agent>) => res.data,
      invalidatesTags: ['AgentList'],
    }),
    listModels: builder.query<SupportedModel[], void>({
      query: () => '/api/v1/agents/models',
      transformResponse: (res: ApiResponse<SupportedModel[]>) => res.data,
    }),
    listToolkits: builder.query<Toolkit[], void>({
      query: () => '/api/v1/agents/toolkits',
      transformResponse: (res: ApiResponse<Toolkit[]>) => res.data,
    }),
    listTools: builder.query<AgentTool[], void>({
      query: () => '/api/v1/tools',
      transformResponse: (res: ApiResponse<AgentTool[]>) => res.data,
      providesTags: ['ToolCatalogue'],
    }),
    /** Updates the tools catalogue entry itself — the single source of truth for output_schema /
     * status_message_override, shared by every agent that attaches this tool (see AgentTool). */
    updateTool: builder.mutation<AgentTool, { tool_id: string; body: Partial<AgentTool> }>({
      query: ({ tool_id, body }) => ({ url: `/api/v1/tools/${tool_id}`, method: 'PUT', body }),
      transformResponse: (res: ApiResponse<AgentTool>) => res.data,
      invalidatesTags: ['ToolCatalogue'],
    }),
    listScreens: builder.query<ScreensInfo, void>({
      query: () => '/api/v1/agents/screens',
      transformResponse: (res: ApiResponse<ScreensInfo>) => res.data,
    }),
    getParameterCatalogue: builder.query<ParameterCatalogue, void>({
      query: () => '/api/v1/agents/parameter-catalogue',
      transformResponse: (res: ApiResponse<ParameterCatalogue>) => res.data,
    }),
    listAvailableAgents: builder.query<Agent[], void>({
      query: () => '/api/v1/agents/available',
      transformResponse: (res: ApiResponse<Agent[]>) => res.data,
    }),
    uploadAgentFiles: builder.mutation<AgentFile[], FormData>({
      query: (formData) => ({ url: '/api/v1/agents/upload', method: 'POST', body: formData }),
      transformResponse: (res: ApiResponse<AgentFile[]>) => res.data,
    }),
    listAgentRuns: builder.query<{ data: AgentRun[]; total: number }, string>({
      query: (id) => `/api/v1/agents/${id}/runs`,
      transformResponse: (res: ApiResponse<AgentRun[]>) => ({
        data: res.data,
        total: res.total ?? res.data.length,
      }),
      providesTags: (_r, _e, id) => [{ type: 'Agent', id: `${id}_runs` }],
    }),
    triggerAgentRun: builder.mutation<AgentRun, string>({
      query: (id) => ({ url: `/api/v1/agents/${id}/runs`, method: 'POST', body: {} }),
      transformResponse: (res: ApiResponse<AgentRun>) => res.data,
      invalidatesTags: (_r, _e, id) => [{ type: 'Agent', id: `${id}_runs` }],
    }),
    deleteAgentFile: builder.mutation<void, string>({
      query: (fileId) => ({ url: `/api/v1/agents/upload/${fileId}`, method: 'DELETE' }),
    }),
    previewAgentPrompt: builder.mutation<PromptPreview, { agent_id: string; module?: string; tool_scope_overrides?: unknown }>({
      query: (body) => ({ url: '/api/v1/agents/preview-prompt', method: 'POST', body }),
      transformResponse: (res: ApiResponse<PromptPreview>) => res.data,
    }),
  }),
});

export const {
  useListAgentsQuery,
  useGetAgentQuery,
  useCreateAgentMutation,
  useUpdateAgentMutation,
  useSetAgentStatusMutation,
  useDeleteAgentMutation,
  useCloneAgentMutation,
  useListModelsQuery,
  useListToolkitsQuery,
  useListToolsQuery,
  useUpdateToolMutation,
  useListScreensQuery,
  useGetParameterCatalogueQuery,
  useListAvailableAgentsQuery,
  useUploadAgentFilesMutation,
  useListAgentRunsQuery,
  useTriggerAgentRunMutation,
  useDeleteAgentFileMutation,
  usePreviewAgentPromptMutation,
} = agentsApi;
